import { latest } from "../lib/atom-crdt"
import { test, expect, equals } from "@benchristel/taste"
import { xprod as crossProduct, applyTo as pass, pipe } from "ramda"
import {
  withTitle,
  withCandidate,
  withBallot,
  withCandidateDeleted,
} from "./transformers"

import { PollCRDT, alphabetically, emptyPoll } from "./PollCRDT"
import { mergeBallots } from "./mergeBallots"

export function merge(a: PollCRDT, b: PollCRDT): PollCRDT {
  return {
    initial: a.initial && b.initial,
    title: latest(a.title, b.title, alphabetically),
    candidates: { ...a.candidates, ...b.candidates },
    ballots: mergeBallots(a.ballots, b.ballots),
  }
}

const newPoll = (title: string) => withTitle(title, emptyPoll())

test("merge", {
  "preserves emptiness"() {
    expect(merge(emptyPoll(), emptyPoll()), equals, emptyPoll())
  },

  "preserves non-emptiness"() {
    expect(merge(emptyPoll(), newPoll("")), equals, newPoll(""))
    expect(merge(newPoll(""), emptyPoll()), equals, newPoll(""))
  },

  "merges lists of candidates"() {
    let pollA = newPoll("What appetizer to order?")
    let pollB = pollA
    pollA = withCandidate({ id: "1", content: "nachos" }, pollA)
    pollB = withCandidate({ id: "2", content: "jalapeños" }, pollA)
    const merged = merge(pollA, pollB)
    expect(Object.values(merged.candidates).sort(), equals, [
      "jalapeños",
      "nachos",
    ])
  },

  "merges titles"() {
    const pollA = newPoll("What best icecream?")
    const pollB = withTitle("Which icecream is favorite?", pollA)
    expect(merge(pollA, pollB), equals, pollB)
  },

  "merges a null title with a non-null title"() {
    // const a: PollCRDT = { title: { value: "Hello, world?" } }
    const a: PollCRDT = newPoll("Hello, world?")
    const b: PollCRDT = emptyPoll()
    expect(merge(b, a).title.value, equals, "Hello, world?")
    expect(merge(a, b).title.value, equals, "Hello, world?")
  },

  "with title is associative"() {
    const a = newPoll("Who is best barber in town?")
    const b = newPoll("Where is best city?")
    const c = newPoll("Why?")

    expect(merge(merge(a, b), c), equals, merge(a, merge(b, c)))
  },

  "with candidate and title is idempotent"() {
    let pollA = withCandidate(
      {
        id: "1",
        content: "nachos",
      },
      newPoll("What appetizer to order?"),
    )

    const merged = merge(pollA, JSON.parse(JSON.stringify(pollA)))
    expect(merged, equals, pollA)
  },
})

const propertyTestCases: { name: string; createPoll(): PollCRDT }[] = [
  {
    name: "empty poll",
    createPoll: emptyPoll,
  },
  {
    name: "a poll with a title",
    createPoll: () => withTitle("title1", emptyPoll()),
  },
  {
    name: "a poll with a candidate",
    createPoll: () =>
      withCandidate(
        {
          id: "1",
          content: "Chocolate",
        },
        withTitle("title1", emptyPoll()),
      ),
  },
  {
    name: "a poll with two candidates",
    createPoll: () =>
      pass(
        emptyPoll(),
        pipe(
          withTitle("title1"),
          withCandidate({ id: "1", content: "Chocolate" }),
          withCandidate({ id: "2", content: "Vanilla" }),
        ),
      ),
  },
  {
    name: "a poll with a deleted candidate",
    createPoll: () =>
      pass(
        emptyPoll(),
        pipe(
          withTitle("title1"),
          withCandidate({ id: "rlphndr", content: "Ralph Nader" }),
          withCandidateDeleted("rlphndr"),
        ),
      ),
  },
  {
    name: "a poll with an empty ballot",
    createPoll: () => pass(emptyPoll(), pipe(withBallot("protagonist-id", []))),
  },
  {
    name: "a poll with an updated ballot",
    createPoll: () =>
      pass(
        emptyPoll(),
        pipe(
          withBallot("protagonist-id", []),
          withBallot("protagonist-id", ["tofu"]),
        ),
      ),
  },
  {
    name: "a poll with two ballots",
    createPoll: () =>
      pass(
        emptyPoll(),
        pipe(
          withBallot("protagonist-id", ["seitan"]),
          withBallot("antagonist-id", ["tofu"]),
        ),
      ),
  },
  {
    name: "a poll in which the protagonist has voted once",
    createPoll: () =>
      pass(
        emptyPoll(),
        pipe(withBallot("protagonist-id", ["collision-favorite"])),
      ),
  },
]

test(
  "merge is idempotent",
  propertyTestCases.reduce(
    (memo, testCase) => ({
      ...memo,
      [`given ${testCase.name}`]() {
        const poll = testCase.createPoll()
        expect(merge(poll, poll), equals, poll)
      },
    }),
    {},
  ),
)
test(
  "merge is commutative",
  crossProduct(propertyTestCases, propertyTestCases).reduce(
    (memo, [case1, case2]) => ({
      ...memo,
      [`given ${case1.name} and ${case2.name}`]() {
        const poll1 = case1.createPoll()
        const poll2 = case2.createPoll()
        expect(merge(poll1, poll2), equals, merge(poll2, poll1))
      },
    }),
    {},
  ),
)
